import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { Store } from '@ngrx/store';

import { Category } from '@fcom/dapi/api/models/category';
import { FinnairItineraryItemFlight } from '@fcom/dapi/api/models/finnair-itinerary-item-flight';
import { FinnairPassengerCode } from '@fcom/dapi/api/models/finnair-passenger-code';
import { FinnairPassengerItem } from '@fcom/dapi/api/models/finnair-passenger-item';
import { cartPassengersSelector } from '@fcom/booking/store/selectors/cart.selector';
import { bundlesByCategorySelector } from '@fcom/common-booking/store/selectors/bundle.selector';
import { asTravelerService } from '@fcom/common-booking/store/utils/services.utils';
import { ServiceSelections } from '@fcom/dapi/interfaces/services.interface';
import { serviceSelections } from '@fcom/common-booking/store/selectors/services.selector';
import { serviceCategoriesMapSelector } from '@fcom/common-booking/store/selectors/service-catalog.selector';
import * as ServicesActions from '@fcom/common-booking/store/actions/services.actions';
import { MonitoringLogger } from '@fcom/core/services/monitoring/monitoring-logger';

import { getServicesNotifications } from '../../utils/ancillaries.utils';
import { ConstituentPair, EligibilitiesForFragments, Notification } from '../../interfaces/ancillaries-interface';
import { SmpProduct } from '../product-list/product-list.component';

@Component({
  selector: 'fin-ancillary-bundle-selection',
  templateUrl: './ancillary-bundle-selection.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: { class: 'block' },
})
export class AncillaryBundleSelectionComponent {
  readonly category = input.required<Category>();
  readonly flight = input.required<FinnairItineraryItemFlight>();
  readonly product = input<SmpProduct | undefined>();
  readonly hideZeroPrices = input(false);
  readonly availability = input<EligibilitiesForFragments>({});

  private store$ = inject(Store);
  private logger = inject(MonitoringLogger);

  private selections = this.store$.selectSignal(serviceSelections);

  private cartPassengers = this.store$.selectSignal(cartPassengersSelector);
  readonly passengers = computed(() =>
    (this.cartPassengers() || []).filter(({ passengerTypeCode }) => passengerTypeCode !== FinnairPassengerCode.INF)
  );

  readonly selectionsByPax = computed(() => {
    const selectionsForCategory = this.selections()?.[this.category() as keyof ServiceSelections];
    return selectionsForCategory?.[this.flight()?.id] ?? {};
  });

  private bundlesByCategory = this.store$.selectSignal(bundlesByCategorySelector);
  private bundleForFlight = computed(() => this.bundlesByCategory()[this.category()]?.[this.flight()?.id]);

  readonly constituents = computed<ConstituentPair | undefined>(() => {
    const bundle = this.bundleForFlight();
    if (!bundle) {
      return undefined;
    }

    if (bundle.constituents.length !== 2) {
      this.logger.error('Could not resolve bundle constituents', {
        bundle,
        flightId: this.flight()?.id,
        category: this.category(),
      });

      return undefined;
    }
    return bundle.constituents as ConstituentPair;
  });

  // A present catalog item means the combo is on offer to that passenger, at the price it carries.
  readonly bundleItemByPax = computed(() => this.bundleForFlight()?.bundleItemByPax ?? {});

  private catalogServicesByCategory = this.store$.selectSignal(serviceCategoriesMapSelector);

  readonly notifications = computed<Notification[]>(() => {
    const flight = this.flight();
    if (!flight) {
      return [];
    }
    const services = this.catalogServicesByCategory()?.[this.category()]?.[flight.id] ?? {};
    return getServicesNotifications(services, this.availability() ?? {}, this.category(), flight);
  });

  readonly selectionBlocked = computed<boolean>(() =>
    this.notifications().some(({ blockSelection }) => blockSelection)
  );

  addBundle(passenger: FinnairPassengerItem): void {
    const bundleItem = this.bundleItemByPax()[passenger.id];
    if (!bundleItem) {
      return;
    }
    this.store$.dispatch(
      ServicesActions.setSelectionForFragmentForTraveler({
        category: this.category(),
        fragmentId: this.flight().id,
        travelerId: passenger.id,
        selection: asTravelerService(bundleItem),
      })
    );
  }

  removeBundle(passenger: FinnairPassengerItem): void {
    this.store$.dispatch(
      ServicesActions.setSelectionForFragmentForTraveler({
        category: this.category(),
        fragmentId: this.flight().id,
        travelerId: passenger.id,
        selection: undefined,
      })
    );
  }
}
